import React from 'react';
import { Box, Provider as BumbagProvider } from 'bumbag';
import { ErrorBoundary } from 'react-error-boundary';
import { LeverProvider } from 'react-lever';
import { LoadsProvider } from 'react-loads';
import { BrowserRouter, Route, Switch } from 'react-router-dom';

import ErrorPage from '@medipass/react-core/Layout/ErrorPage';
import LoadingPage from '@medipass/react-core/Layout/LoadingPage';
import NetworkCheck from '@medipass/react-core/NetworkCheck';
import { Provider as ReactCoreProvider } from '@medipass/react-core/state';
import env from '@medipass/utils/get-env';
import * as sentry from '@medipass/utils/sentry';
import sdk from '@medipass/web-sdk';

import getRoutes from 'utils/getRoutes';
import features from './features';

import appPackage from '../package.json';
import * as ApplicationContext from './context/ApplicationContext';
import bumbagTheme from './theme/index';

import '@medipass/ui-kit/css/hinter.css';
import './index.css';

if (typeof window !== 'undefined') {
  env.setup({
    local: {
      INTERCOM_APP_ID: 'nchsvymn',
      HPF_SCRIPT_URL: 'https://gateway.test.nab.com.au/form/v3/hpf.js',
      APP_URL: 'http://localhost:9000',
    },
    dev: {
      INTERCOM_APP_ID: 'nchsvymn',
      HPF_SCRIPT_URL: 'https://gateway.test.nab.com.au/form/v3/hpf.js',
      APP_URL: 'https://dev-my.medipass.io',
    },
    staging: {
      INTERCOM_APP_ID: 'nchsvymn',
      HPF_SCRIPT_URL: 'https://gateway.test.nab.com.au/form/v3/hpf.js',
      APP_URL: 'https://stg-my.medipass.io',
    },
    perf: {
      INTERCOM_APP_ID: 'nchsvymn',
      HPF_SCRIPT_URL: 'https://gateway.test.nab.com.au/form/v3/hpf.js',
      APP_URL: 'https://perf-my.medipass.io',
    },
    dr: {
      INTERCOM_APP_ID: 'vihmz509',
      HPF_SCRIPT_URL: 'https://gateway.nab.com.au/form/v3/hpf.js',
      APP_URL: ['https://dr-my.medipass.io'],
    },
    preprod: {
      INTERCOM_APP_ID: 'vihmz509',
      HPF_SCRIPT_URL: 'https://gateway.nab.com.au/form/v3/hpf.js',
      APP_URL: 'https://my-blue.medipass.io',
      GOOGLE_MERCHANT_ID: 'BCR2DN6T7POI75AQ',
    },
    prod: {
      INTERCOM_APP_ID: 'vihmz509',
      HPF_SCRIPT_URL: 'https://gateway.nab.com.au/form/v3/hpf.js',
      APP_URL: 'https://my.medipass.io',
      GOOGLE_MERCHANT_ID: 'BCR2DN6T7POI75AQ',
    },
  });
}

// Initialise the Tyro Health Web SDK
sdk.setup(env.ENV_NAME as string, {
  appId: 'consumer-web-application',
  appVersion: appPackage.version,
});

sentry.setup({
  environment: env.ENV_NAME,
  version: sdk.appVersion,
  url: 'https://55b9da7e63604c45ac80bccc13bfffce@sentry.io/1210947',
});

const { preserved, routes } = getRoutes();

function App() {
  const isDev = React.useState(env.ENV_NAME === 'dev' || env.ENV_NAME === 'staging');

  // @ts-expect-error
  const NotFound = preserved?.['404'] || React.Fragment;

  return (
    <LoadsProvider>
      {/* @ts-expect-error */}
      <ReactCoreProvider router="reach-router">
        {/* @ts-expect-error TS(2322): Type '{ global: { styles: { base: (props: any) => ... */}
        <BumbagProvider theme={bumbagTheme}>
          {/* @ts-expect-error */}
          <ErrorBoundary FallbackComponent={ErrorPage}>
            <ApplicationContext.Provider>
              <LeverProvider isDev={isDev} features={features}>
                <Box>
                  <React.Suspense fallback={<LoadingPage />}>
                    <BrowserRouter>
                      <Switch>
                        {routes.map(({ path, component: Component = React.Fragment }) => (
                          <Route key={path} path={path} component={Component} exact={true} />
                        ))}
                        <Route path="*" component={NotFound} />
                      </Switch>
                    </BrowserRouter>
                    <NetworkCheck />
                  </React.Suspense>
                </Box>
              </LeverProvider>
            </ApplicationContext.Provider>
          </ErrorBoundary>
        </BumbagProvider>
      </ReactCoreProvider>
    </LoadsProvider>
  );
}

export default App;
